import React, { useMemo } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { FetchCustomerQuery } from '@/lib/api/customer/customer';
import { useSite } from '@/hooks/siteHooks';
import { FetchDeviceQuery, FetchSubDevicesQuery } from '@/lib/api/devices/devices';
import { getAlert } from '@/features/AdminAlert/alertApi';
import axiosClient, { axiosClientAdmin } from '@/lib/auth';
import { useUser } from '@/features/Users/hooks/useUsers';
import {
  Activity,
  BarChart3,
  Building2,
  Camera as CameraIcon,
  ChevronDown,
  Cog,
  FileText,
  HardDrive,
  Home,
  MapPin,
  Monitor as MonitorIcon,
  MonitorSpeaker,
  Plug,
  Plus,
  Settings,
  Shield,
  Siren,
  Users as UsersIcon,
  UserIcon,
  Network,
  User,
  ChevronsUpDown,
  X,
  UserPlus,
  Folder,
  FolderPlus,
  DollarSign,
  Rocket,
} from 'lucide-react';
import type { MonitoringAlertDto } from '@/features/AdminAlert/alertApi';
import type { MonitorAlertFullDto } from '@/app/Alert/types';
import { Input } from '../ui/input';
import { useState } from 'react';
import { searchCustomers } from '@/features/Customers/customerAPI';
import { Comparator, FilterOperator } from '@/lib/utils';
import { AddCustomer } from '@/features/Customers/AddCustomer';

import { SitesSearchDto } from '@/app/Sites/siteApi';

type CrumbItem = {
  label: string;
  href?: string;
  icon?: JSX.Element;
  popover?: {
    trigger: 'hover' | 'click';
    content: React.ReactNode | null;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end';
  };
}

// Customer Search Component for Popover
function CustomerSearchPopover() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  
  // Use infinite query for pagination
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['breadcrumb-customers-paginated', searchTerm],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      // Use a smaller page size for the first page to load faster
      const actualPageSize = pageParam === 1 ? Math.min(10, 20) : 20;

      // Build search filters
      const filterFilters = [];
      if (searchTerm) {
        // Search across name, accountNumber, and email fields
        filterFilters.push(
          { field: 'name', value: searchTerm, action: Comparator.ILike },
          { field: 'accountNumber', value: searchTerm, action: Comparator.ILike },
          { field: 'email', value: searchTerm, action: Comparator.ILike }
        );
      }

      const response = await searchCustomers({
        pageNumber: pageParam,
        pageSize: actualPageSize,
        orderBy: 'name asc',
        sortBy: 'name',
        includeArchived: false,
        filterOperator: FilterOperator.Or, // Use OR to search across multiple fields
        filterFilters: filterFilters.length > 0 ? filterFilters : undefined,
      });

      const customers = response.rows || [];
      // Filter duplicates by ID
      const uniqueCustomers = customers.filter(
        (customer, index, self) => index === self.findIndex((c) => c.id === customer.id)
      );

      return {
        customers: uniqueCustomers,
        nextPage: (response.totalRows || 0) > pageParam * actualPageSize ? pageParam + 1 : undefined,
        totalRows: response.totalRows || 0,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 300000, // Cache for 5 minutes since this doesn't change often
  });

  // Flatten all pages into a single array
  const customers = data?.pages.flatMap(page => page.customers) || [];

  return (
    <div className="w-80">
      <div className="border-b relative">
        <Input 
          placeholder="Search customers..." 
          className="rounded-none border-0 bg-transparent pr-8 outline-none" 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          isSearch
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-700 rounded"
          >
            <X className="h-3 w-3 text-gray-400 hover:text-white" />
          </button>
        )}
      </div>
      
      <div 
        className="max-h-64 overflow-y-auto"
        onScroll={(e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
          // Load more when scrolled to bottom
          if (scrollHeight - scrollTop <= clientHeight + 50 && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
      >
        {isLoading ? (
          <div className="p-4 text-center text-gray-400">Loading...</div>
        ) : customers.length > 0 ? (
          <>
            {customers.map((customer) => (
              <Link
                key={customer.id}
                to={`/customers/${customer.id}`}
                className="flex items-center gap-2 p-1
                 hover:bg-gray-800  last:border-b-0"
              >
                <Folder className="h-4 w-4 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <div className=" text-white truncate text-sm">{customer.name || ''}</div>
                </div>
              </Link>
            ))}
            {isFetchingNextPage && (
              <div className="p-2 text-center text-gray-400 text-sm">Loading more...</div>
            )}
            {hasNextPage && !isFetchingNextPage && customers.length >= 10 && (
              <button
                onClick={() => fetchNextPage()}
                className="w-full p-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 border-t"
              >
                Load more customers
              </button>
            )}
          </>
        ) : (
          <div className="p-4 text-center text-gray-400">
            {searchTerm ? 'No customers found' : 'Start typing to search'}
          </div>
        )}
      </div>
      
      <div className="p-3 border-t">
        <button 
          onClick={() => setIsAddCustomerOpen(true)}
          className="flex items-center gap-2 text-sm text-lime hover:underline w-full text-left font-semibold"
        >
          <FolderPlus className="h-4 w-4" />
          Add new customer
        </button>
      </div>
      
      <AddCustomer open={isAddCustomerOpen} onOpenChange={setIsAddCustomerOpen} />
    </div>
  );
}

// Site Search Component for Popover
function SiteSearchPopover({ customerId }: { customerId: string }) {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Use infinite query for pagination
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['breadcrumb-sites-paginated', customerId, searchTerm],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      // Use a smaller page size for the first page to load faster
      const actualPageSize = pageParam === 1 ? Math.min(10, 20) : 20;

      // Use customer-specific endpoint with simple parameters like useCustomerSitesQuery
      const response = await axiosClientAdmin.get<{rows: SitesSearchDto[], totalRows: number}>(
        `/Customer/${customerId}/project/search`,
        {
          params: {
            PageNumber: pageParam,
            PageSize: actualPageSize,
            SortBy: 'name',
            SortDir: 'Ascending',
          },
        }
      );

      const sites = response.data.rows || [];
      
      // Apply client-side search filtering if search term exists
      const filteredSites = searchTerm 
        ? sites.filter(site => 
            site.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            site.address1?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : sites;
      
      // Filter duplicates by ID
      const uniqueSites = filteredSites.filter(
        (site, index, self) => index === self.findIndex((s) => s.id === site.id)
      );

      return {
        sites: uniqueSites,
        nextPage: (response.data.totalRows || 0) > pageParam * actualPageSize ? pageParam + 1 : undefined,
        totalRows: response.data.totalRows || 0,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 300000, // Cache for 5 minutes since this doesn't change often
    enabled: Boolean(customerId),
  });

  // Flatten all pages into a single array
  const sites = data?.pages.flatMap(page => page.sites) || [];

  return (
    <div className="w-80">
      <div className="border-b relative">
        <Input 
          placeholder="Search sites..." 
          className="rounded-none border-0 bg-transparent pr-8 outline-none" 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          isSearch
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-700 rounded"
          >
            <X className="h-3 w-3 text-gray-400 hover:text-white" />
          </button>
        )}
      </div>
      
      <div 
        className="max-h-64 overflow-y-auto"
        onScroll={(e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
          // Load more when scrolled to bottom
          if (scrollHeight - scrollTop <= clientHeight + 50 && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
      >
        {isLoading ? (
          <div className="p-4 text-center text-gray-400">Loading...</div>
        ) : sites.length > 0 ? (
          <>
            {sites.map((site) => (
              <Link
                key={site.id}
                to={`/Customer/${customerId}/project/${site.id}`}
                className="flex items-center gap-2 p-1 hover:bg-gray-800 last:border-b-0"
              >
                <Building2 className="h-4 w-4 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate text-sm">{site.name || 'Unnamed Site'}</div>
                  {site.address1 && (
                    <div className="text-xs text-gray-400 truncate">{site.address1}</div>
                  )}
                </div>
              </Link>
            ))}
            {isFetchingNextPage && (
              <div className="p-2 text-center text-gray-400 text-sm">Loading more...</div>
            )}
            {hasNextPage && !isFetchingNextPage && sites.length >= 10 && (
              <button
                onClick={() => fetchNextPage()}
                className="w-full p-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 border-t"
              >
                Load more sites
              </button>
            )}
          </>
        ) : (
          <div className="p-4 text-center text-gray-400">
            {searchTerm ? 'No sites found' : 'Start typing to search'}
          </div>
        )}
      </div>
      
      <div className="p-3 border-t">
        <Link 
          to={`/Customer/${customerId}/site/new`}
          className="flex items-center gap-2 text-sm text-lime hover:underline font-semibold"
        >
          <Plus className="h-4 w-4" />
          Add new site
        </Link>
      </div>
    </div>
  );
}

// Device Search Component for Popover
function DeviceSearchPopover({ customerId, siteId }: { customerId: string; siteId?: string }) {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Use infinite query for pagination
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['breadcrumb-devices-paginated', customerId, siteId, searchTerm],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      let allDevices = [];

      if (siteId) {
        // If siteId is provided, only fetch devices from that specific site
        try {
          const res = await axiosClientAdmin.get(
            `/Customer/${customerId}/project/${siteId}/assets`
          );
          allDevices = (res.data?.rows || []).map((device: any) => ({
            ...device,
            siteName: 'Current Site', // We could fetch site name if needed
            siteId: siteId,
          }));
        } catch (error) {
          console.error(`Error fetching devices for site ${siteId}:`, error);
          allDevices = [];
        }
      } else {
        // If no siteId, fetch devices from all sites (original behavior)
        const actualPageSize = pageParam === 1 ? Math.min(5, 10) : 10;

        // First, get sites for this customer with pagination
        const sitesResponse = await axiosClientAdmin.get<{rows: SitesSearchDto[], totalRows: number}>(
          `/Customer/${customerId}/project/search`,
          {
            params: {
              PageNumber: pageParam,
              PageSize: actualPageSize,
              SortBy: 'name',
              SortDir: 'Ascending',
            },
          }
        );

        const sites = sitesResponse.data.rows || [];
        
        // For each site, fetch its devices
        const devicePromises = sites.map(async (site) => {
          try {
            const res = await axiosClientAdmin.get(
              `/Customer/${customerId}/project/${site.id}/assets`
            );
            // Add site information to each device for grouping
            return (res.data?.rows || []).map((device: any) => ({
              ...device,
              siteName: site.name,
              siteId: site.id,
            }));
          } catch (error) {
            console.error(`Error fetching devices for site ${site.id}:`, error);
            return [];
          }
        });

        // Wait for all device requests to complete
        const devicesArrays = await Promise.all(devicePromises);
        // Flatten the array of arrays into a single array
        allDevices = devicesArrays.flat();
      }
      
      // Apply client-side search filtering if search term exists
      const filteredDevices = searchTerm 
        ? allDevices.filter((device: any) => 
            device.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            device.siteName?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : allDevices;
      
      // Filter duplicates by ID
      const uniqueDevices = filteredDevices.filter(
        (device: any, index: number, self: any[]) => index === self.findIndex((d: any) => d.id === device.id)
      );

      return {
        devices: uniqueDevices,
        nextPage: siteId ? undefined : undefined, // No pagination for single site, could be enhanced later
        totalRows: uniqueDevices.length,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 300000, // Cache for 5 minutes since this doesn't change often
    enabled: Boolean(customerId),
  });

  // Flatten all pages into a single array
  const devices = data?.pages.flatMap(page => page.devices) || [];

  return (
    <div className="w-80">
      <div className="border-b relative">
        <Input 
          placeholder="Search devices..." 
          className="rounded-none border-0 bg-transparent pr-8 outline-none" 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          isSearch
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-700 rounded"
          >
            <X className="h-3 w-3 text-gray-400 hover:text-white" />
          </button>
        )}
      </div>
      
      <div 
        className="max-h-64 overflow-y-auto"
        onScroll={(e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
          // Load more when scrolled to bottom
          if (scrollHeight - scrollTop <= clientHeight + 50 && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
      >
        {isLoading ? (
          <div className="p-4 text-center text-gray-400">Loading...</div>
        ) : devices.length > 0 ? (
          <>
            {devices.map((device) => {
              // Find the correct nvrId from videoProcessorConfigs (same logic as CustomerComponent)
              const nvrId = device.videoProcessorConfigs?.find((x: any) => x?.nvrId)?.nvrId;
              const deviceRouteId = nvrId || device.id;
              
              return (
                <Link
                  key={device.id}
                  to={`/devices/${deviceRouteId}/edit`}
                  className="flex items-center gap-2 p-1 hover:bg-gray-800 last:border-b-0"
                >
                  <HardDrive className="h-4 w-4 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-white truncate text-sm">{device.name || 'Unnamed Device'}</div>
                 
                  </div>
                </Link>
              );
            })}
            {isFetchingNextPage && (
              <div className="p-2 text-center text-gray-400 text-sm">Loading more...</div>
            )}
            {hasNextPage && !isFetchingNextPage && devices.length >= 5 && (
              <button
                onClick={() => fetchNextPage()}
                className="w-full p-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 border-t"
              >
                Load more devices
              </button>
            )}
          </>
        ) : (
          <div className="p-4 text-center text-gray-400">
            {searchTerm ? 'No devices found' : 'Start typing to search'}
          </div>
        )}
      </div>
      
      <div className="p-3 border-t">
        <Link 
          to={`/devices/new`}
          className="flex items-center gap-2 text-sm text-lime hover:underline font-semibold"
        >
          <Plus className="h-4 w-4" />
          Add new device
        </Link>
      </div>
    </div>
  );
}

// Camera Search Component for Popover
function CameraSearchPopover({ deviceId, routeName, icon }: { deviceId: string, routeName: string, icon:any }) {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Use infinite query for pagination
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['breadcrumb-cameras-paginated', deviceId, searchTerm],
    initialPageParam: 1,
    queryFn: async () => {
      // Use the same query logic as the original FetchSubDevicesQuery
      const subDevicesQuery = FetchSubDevicesQuery({ deviceId });
      const allCameras = await subDevicesQuery.queryFn() || [];
      
      // Apply client-side search filtering if search term exists
      const filteredCameras = searchTerm 
        ? allCameras.filter((camera: any) => 
            camera.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            camera.id?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : allCameras;
      
      // Filter duplicates by ID
      const uniqueCameras = filteredCameras.filter(
        (camera: any, index: number, self: any[]) => index === self.findIndex((c: any) => c.id === camera.id)
      );

      return {
        cameras: uniqueCameras,
        nextPage: undefined, // No pagination needed for cameras typically
        totalRows: uniqueCameras.length,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 300000, // Cache for 5 minutes since this doesn't change often
    enabled: Boolean(deviceId),
  });

  // Flatten all pages into a single array
  const cameras = data?.pages.flatMap(page => page.cameras) || [];

  return (
    <div className="w-80">
      <div className="border-b relative">
        <Input 
          placeholder="Search cameras..." 
          className="rounded-none border-0 bg-transparent pr-8 outline-none" 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          isSearch
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-700 rounded"
          >
            <X className="h-3 w-3 text-gray-400 hover:text-white" />
          </button>
        )}
      </div>
      
      <div 
        className="max-h-64 overflow-y-auto"
        onScroll={(e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
          // Load more when scrolled to bottom
          if (scrollHeight - scrollTop <= clientHeight + 50 && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
      >
        {isLoading ? (
          <div className="p-4 text-center text-gray-400">Loading...</div>
        ) : cameras.length > 0 ? (
          <>
            {cameras.map((camera: any) => (
              <Link
                key={camera.id}
                to={`/devices/${deviceId}/${routeName}/${camera.id}`}
                className="flex items-center gap-2 p-1 hover:bg-gray-800 last:border-b-0"
              >
                {icon}
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate text-sm">{camera.name || 'Unnamed Camera'}</div>
                </div>
              </Link>
            ))}
            {isFetchingNextPage && (
              <div className="p-2 text-center text-gray-400 text-sm">Loading more...</div>
            )}
            {hasNextPage && !isFetchingNextPage && cameras.length >= 5 && (
              <button
                onClick={() => fetchNextPage()}
                className="w-full p-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 border-t"
              >
                Load more cameras
              </button>
            )}
          </>
        ) : (
          <div className="p-4 text-center text-gray-400">
            {searchTerm ? 'No cameras found' : 'Start typing to search'}
          </div>
        )}
      </div>
    </div>
  );
}

export function AppBreadcrumbs() {
  const location = useLocation();
  const path = location.pathname;

  // Match known dynamic routes
  const mCustomers = matchPath({ path: '/customers', end: true }, path);
  const mCustomerDetail = matchPath('/customers/:customerId', path);
  const mCustomerSite =
    matchPath('/Customer/:companyId/project/:siteId/*', path) ||
    matchPath('/Customer/:companyId/project/:siteId', path);
  const mDevices = matchPath({ path: '/devices', end: true }, path);
  const mDeviceEdit = matchPath('/devices/:deviceId/edit', path) || matchPath('/devices/:id/edit', path);
  const mDeviceCamera = matchPath('/devices/:deviceId/camera/:cameraId', path);
  const mReporting = matchPath({ path: '/reporting', end: true }, path);
  const mBilling = matchPath({ path: '/billing', end: true }, path);
  const mAlerts = matchPath({ path: '/alerts', end: true }, path);
  const mMonitor = matchPath({ path: '/monitor', end: true }, path);
  const mMonitoringOverview = matchPath({ path: '/monitoring-overview', end: true }, path);
  const mMonitoringOverviewV2 = matchPath({ path: '/monitoring-overview-v2', end: true }, path);
  const mMonitoringAlert = matchPath('/monitoring/alert/:alertId', path);
  const mMonitoringAlertReport = matchPath('/monitoring/alert/:alertId/report', path);
  const mAlertSimple = matchPath('/alert/:alertId', path);
  const mReviewAlert = matchPath('/review/:alertId', path);
  const isAlertSimplePath = /^\/alert\/[^/]+$/.test(path);
  const isReviewAlertPath = /^\/review\/[^/]+$/.test(path);
  const mConnectionsTop = matchPath({ path: '/connections', end: true }, path);
  const mConnectionsDeep = matchPath('/connections/*', path);
  const mUsers = matchPath('/users/*', path) || matchPath({ path: '/users', end: true }, path);
  const mUserDetail = matchPath('/users/:userId', path);
  const mTeams = matchPath({ path: '/team', end: true }, path);
  const mTeamDetail = matchPath('/tenant/:tenantId/team/:teamId', path);
  const mIHub = matchPath('/ihub/*', path);
  const mIHubMonitor = matchPath('/ihub/monitor', path);
  const mIHubIntegration = matchPath('/ihub/integration/:integrationId', path);

  const mSystem = matchPath('/system', path);
  const mIngestion = matchPath('/ingestion', path);
  const mIngestionLegacy = matchPath('/ingestion/legacy', path);
  const mAlertAudit = matchPath('/alert-audit', path);
  const mSitesList = matchPath({ path: '/sites', end: true }, path);
  const mSiteShallow = matchPath('/site/:siteId', path);
  const mSiteNew = matchPath('/site/new', path) || matchPath('/Customer/:companyId/site/new', path);
  const mViewer = matchPath({ path: '/viewer', end: true }, path);
  const mDashboard = matchPath({ path: '/', end: true }, path) || matchPath({ path: '/dashboard', end: true }, path) || matchPath({ path: '/home', end: true }, path);
  const mEdgeLink = matchPath({ path: '/edgelink', end: true }, path);
  const mEdgeLinkAdd = matchPath({ path: '/edgelink/add', end: true }, path);
  const mEdgeLinkDetail = matchPath('/edgelink/:bridgeId', path);
  const mEdgeLinkDeployments = matchPath({ path: '/edgelink-deployments', end: true }, path);
  const mUserAdd = matchPath('/users/add', path);
  const mTeamAdd = matchPath('/team/add', path);

  // Data queries for labels
  const customerIdRoute = mCustomerDetail?.params['customerId'] ?? mCustomerSite?.params['companyId'];
  const siteIdRoute = mCustomerSite?.params['siteId'] ?? mSiteShallow?.params['siteId'];
  
  // Get viewer state from URL params when on viewer page
  const viewerCustomerId = mViewer ? new URLSearchParams(location.search).get('customerId') : null;
  const viewerSiteId = mViewer ? new URLSearchParams(location.search).get('siteId') : null;
  const viewerDeviceId = mViewer ? new URLSearchParams(location.search).get('deviceId') : null;
  
  // Get user ID for user detail pages
  const userIdRoute = mUserDetail?.params['userId'];
  
  // Get team ID and tenant ID for team detail pages
  const teamIdRoute = mTeamDetail?.params['teamId'];
  const tenantIdRoute = mTeamDetail?.params['tenantId'];
  const deviceEditParams = (mDeviceEdit?.params as Record<string, string> | undefined);
  let deviceId = deviceEditParams?.deviceId ?? deviceEditParams?.id ?? mDeviceCamera?.params['deviceId'] ?? viewerDeviceId;
  const deviceIdFromPath = path.startsWith('/devices/') ? path.split('/')[2] : undefined;
  if (!deviceId && deviceIdFromPath) {
    deviceId = deviceIdFromPath;
  }
  const cameraId = mDeviceCamera?.params['cameraId'];

  // Device (load first so we can derive customer/site for breadcrumbs)
  const { data: device } = useQuery({
    ...FetchDeviceQuery({ deviceId: deviceId ?? '' }),
    enabled: Boolean(deviceId),
  });

  const effectiveCustomerId = customerIdRoute ?? viewerCustomerId ?? device?.customerId;
  const effectiveSiteId = siteIdRoute ?? viewerSiteId ?? device?.projectId ?? device?.siteId;

  const { data: customer } = useQuery({
    ...FetchCustomerQuery({ customerId: effectiveCustomerId ?? '' }),
    enabled: Boolean(effectiveCustomerId),
  });

  const siteQuery = useSite({
    siteId: effectiveSiteId ?? '',
    companyId: (effectiveCustomerId as string) ?? '',
    enabled: Boolean(effectiveCustomerId && effectiveSiteId),
  });
  const siteName = siteQuery.data?.name;

  const { data: subDevices } = useQuery({
    ...FetchSubDevicesQuery({ deviceId: deviceId ?? '' }),
    enabled: Boolean(deviceId && cameraId),
  });
  const cameraName = useMemo(() => {
    if (!cameraId || !subDevices) return undefined;
    return subDevices.find((s) => s.id === cameraId)?.name ?? cameraId;
  }, [subDevices, cameraId]);

  // Monitoring Alert (for breadcrumb short code)
  const alertIdParam = (mMonitoringAlert || mMonitoringAlertReport || mAlertSimple || mReviewAlert)?.params['alertId']
    ?? (isAlertSimplePath ? path.split('/')[2] : undefined)
    ?? (isReviewAlertPath ? path.split('/')[2] : undefined);
  const queryClient = useQueryClient();
  // Use the same query key as the alert page to share cached data
  const isAdminAlertRoute = Boolean(mMonitoringAlert || mMonitoringAlertReport);
  const cachedFullAlert = alertIdParam
    ? (queryClient.getQueryData<MonitorAlertFullDto>(['fullAlert', alertIdParam])
      ?? queryClient.getQueryData<MonitorAlertFullDto>(['alert', alertIdParam]))
    : undefined;
  const { data: fullAlert } = useQuery({
    queryKey: [isAdminAlertRoute ? 'fullAlert' : 'alert', alertIdParam],
    queryFn: async () => isAdminAlertRoute
      ? (await axiosClientAdmin.get(`/Monitoring/alerts/${alertIdParam}/full`)).data?.alert
      : (await axiosClient.get(`/AlertMonitoring/${alertIdParam}/full`)).data?.alert,
    enabled: Boolean(alertIdParam) && !cachedFullAlert,
  });
  const alertForBreadcrumb = cachedFullAlert || fullAlert;
  const alertShort = useMemo(() => {
    // Prefer human-friendly identifiers; skip UUID-like strings
    const candidates = [
      alertForBreadcrumb?.alertNumber,
      alertForBreadcrumb?.alertId,
      alertForBreadcrumb?.events?.[0]?.alertNumber,
    ].filter(Boolean) as string[];

    const isUuidLike = (s: string) => /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|[0-9a-f]{32})$/i.test(s);
    const nonUuid = candidates.find((c) => !isUuidLike(c));
    if (nonUuid) return nonUuid;
    return alertIdParam ? alertIdParam.substring(0, 8) : undefined;
  }, [alertForBreadcrumb, alertIdParam]);

  // User data for user detail pages
  const { data: user } = useUser(userIdRoute ?? '');
  
  // Team data for team detail pages
  const { data: teamResponse } = useQuery({
    queryKey: ['breadcrumb-team', tenantIdRoute, teamIdRoute],
    queryFn: async () => (await axiosClientAdmin.get(`/tenants/${tenantIdRoute}/TenantMonitoring/companies/${teamIdRoute}`)).data,
    enabled: Boolean(teamIdRoute && tenantIdRoute),
  });
  const team = teamResponse?.monitoringCompany;

  const items: CrumbItem[] = useMemo(() => {
    const list: CrumbItem[] = [];

    // Customers
    if (mCustomers) {
      list.push({ 
        label: 'Customers', 
        href: '/customers', 
        icon: <Folder className="h-4 w-4 text-white" />,
      
      });
      return list;
    }
    if (mCustomerDetail) {
      list.push({ label: customer?.name || 'Unnamed Customer', icon: <Folder className="h-4 w-4 text-white" />,  popover: {
          trigger: 'click',
          content: <CustomerSearchPopover />,
          placement: 'bottom-start'
        } });
      return list;
    }

    // Admin Customer > Site
    if (mCustomerSite) {
     
      if (customerIdRoute) {
        list.push({ label: customer?.name || 'Unnamed Customer', icon: <Folder className="h-4 w-4 text-white" />, href: `/customers/${customerIdRoute}`, popover: {
          trigger: 'click',
          content: <CustomerSearchPopover />,
          placement: 'bottom-start'
        } });
      }
      list.push({
        label: siteName || 'Unnamed Site',
        icon: <Building2 className="h-4 w-4 text-white" />,
        href: `/Customer/${effectiveCustomerId}/project/${effectiveSiteId}`,
        popover: mCustomerSite.params['companyId'] ? {
          trigger: 'click',
          content: <SiteSearchPopover customerId={mCustomerSite.params['companyId']} />,
          placement: 'bottom-start'
        } : undefined
      });
      // Site is the last item, make it white
      list[list.length - 1].icon = <Building2 className="h-4 w-4 text-white" />;
      return list;
    }

    // Sites
    if (mSitesList) {
      list.push({ label: 'Sites', href: '/sites', icon: <Building2 className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mSiteShallow) {
      list.push({ label: 'Sites', href: '/sites', icon: <Building2 className="h-4 w-4 text-lime" /> });
      list.push({ label: 'Unnamed Site', icon: <Building2 className="h-4 w-4 text-white" />, });
      return list;
    }
    if (mSiteNew) {
      list.push({ label: 'Sites', href: '/sites', icon: <Building2 className="h-4 w-4 text-lime" /> });
      list.push({ label: 'New', icon: <Plus className="h-4 w-4 text-white" /> });
      return list;
    }

    // Devices
    if (mDevices) {
      list.push({ label: 'Devices', href: '/devices', icon: <HardDrive className="h-4 w-4 text-white" /> });
      return list;
    }
    const isDeviceEditRoute = Boolean(mDeviceEdit) || /^\/devices\/[^/]+\/edit$/.test(path);
    if (isDeviceEditRoute) {
      // customer > site > device name
      if (effectiveCustomerId) {
        list.push({ label: customer?.name || 'Unnamed Customer', href: `/customers/${effectiveCustomerId}`, icon: <Folder className="h-4 w-4 text-white" />,  popover: {
          trigger: 'click',
          content: <CustomerSearchPopover />,
          placement: 'bottom-start'
        } });
      }
      if (effectiveCustomerId && effectiveSiteId) {
        list.push({ 
          label: siteName || 'Unnamed Site', 
          href: `/Customer/${effectiveCustomerId}/project/${effectiveSiteId}`, 
          icon: <Building2 className="h-4 w-4 text-white" />,
          popover: {
            trigger: 'click',
            content: <SiteSearchPopover customerId={effectiveCustomerId} />,
            placement: 'bottom-start'
          }
        });
      }
      // Device name as final crumb (page)
      list.push({ 
        label: device?.name || 'Unnamed Device', 
        icon: <HardDrive className="h-4 w-4 text-white" />,
        popover: effectiveCustomerId ? {
          trigger: 'click',
          content: <DeviceSearchPopover customerId={effectiveCustomerId} siteId={effectiveSiteId} />,
          placement: 'bottom-start'
        } : undefined
      });
      return list;
    }
    if (mDeviceCamera) {
      // customer > site > device name > camera name
      if (effectiveCustomerId) {
        list.push({ label: customer?.name || 'Unnamed Customer', icon: <Folder className="h-4 w-4 text-white" />,  popover: {
          trigger: 'click',
          content: <CustomerSearchPopover />,
          placement: 'bottom-start'
        } });
      }
      if (effectiveCustomerId && effectiveSiteId) {
        list.push({ 
          label: siteName || 'Unnamed Site', 
          href: `/Customer/${effectiveCustomerId}/project/${effectiveSiteId}`, 
          icon: <Building2 className="h-4 w-4 text-white" />,
          popover: {
            trigger: 'click',
            content: <SiteSearchPopover customerId={effectiveCustomerId} />,
            placement: 'bottom-start'
          }
        });
      }
      list.push({ 
        label: device?.name || 'Unnamed Device', 
        href: `/devices/${mDeviceCamera.params['deviceId']}/edit`, 
        icon: <HardDrive className="h-4 w-4 text-white" />,
        popover: effectiveCustomerId ? {
          trigger: 'click',
          content: <DeviceSearchPopover customerId={effectiveCustomerId} siteId={effectiveSiteId} />,
          placement: 'bottom-start'
        } : undefined
      });
      list.push({ 
        label: cameraName || 'Unnamed Camera', 
        icon: <CameraIcon className="h-4 w-4 text-white" />,
        popover: mDeviceCamera.params['deviceId'] ? {
          trigger: 'click',
          content: <CameraSearchPopover icon={<CameraIcon className="h-4 w-4 text-white" />} routeName="camera" deviceId={mDeviceCamera.params['deviceId']} />,
          placement: 'bottom-start'
        } : undefined
      });
      return list;
    }

    // Monitoring/Alerts/Reporting
    if (mMonitor) {
      list.push({ label: 'Monitoring', icon: <MonitorIcon className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mMonitoringAlert || mMonitoringAlertReport || mAlertSimple || mReviewAlert || isAlertSimplePath || isReviewAlertPath) {
      if (mMonitoringAlert || mMonitoringAlertReport) {
        // For /monitoring/alert/* routes, show full breadcrumbs: Alert Log > Customer > Site > Alert
        list.push({ label: 'Alert Log', href: '/alerts', icon: <Siren className="h-4 w-4 text-lime" /> });
        
        // Add Customer breadcrumb if available
        const alertData = alertForBreadcrumb;
        if (alertData?.companyId && alertData?.companyName) {
          list.push({ 
            label: alertData.companyName, 
            href: `/customers/${alertData.companyId}`, 
            icon: <Folder className="h-4 w-4 text-lime" />,
            popover: {
              trigger: 'click',
              content: <CustomerSearchPopover />,
              placement: 'bottom-start'
            }
          });
        }
        
        // Add Site breadcrumb if available
        if (alertData?.companyId && alertData?.projectId && alertData?.projectName) {
          list.push({ 
            label: alertData.projectName, 
            href: `/Customer/${alertData.companyId}/project/${alertData.projectId}`, 
            icon: <Building2 className="h-4 w-4 text-lime" />,
            popover: {
              trigger: 'click',
              content: <SiteSearchPopover customerId={alertData.companyId} />,
              placement: 'bottom-start'
            }
          });
        }
        
        // Add Device breadcrumb if available (from first event or project assets)
        const firstEvent = alertData?.events?.[0];
        const videoProcessorId = firstEvent?.videoProcessorId;
        
        // Find the device from project assets by matching videoProcessorId
        const deviceAsset = alertData?.project?.assets?.find((asset: any) =>
          asset.cameras?.some((camera: any) => camera.videoProcessorId === videoProcessorId)
        );
        
        // Get the nvrId from the event or try to find it from the asset
        const nvrId = firstEvent?.nvrId || deviceAsset?.nvrId || deviceAsset?.id;
        
        if (nvrId || deviceAsset) {
          const deviceName = deviceAsset?.name || 'Device';
          list.push({ 
            label: deviceName, 
            href: nvrId ? `/devices/${nvrId}/edit` : undefined, 
            icon: <HardDrive className="h-4 w-4 text-lime" />,
            popover: alertData?.companyId ? {
              trigger: 'click',
              content: <DeviceSearchPopover customerId={alertData.companyId} siteId={alertData.projectId} />,
              placement: 'bottom-start'
            } : undefined
          });
        }
      } else {
        list.push({ label: 'Monitoring', href: '/monitor', icon: <MonitorIcon className="h-4 w-4 text-lime" /> });
      }
      list.push({ label: alertShort ?? 'Alert', icon: <Siren className="h-4 w-4 text-lime" />, href: `/monitoring/alert/${alertIdParam}` });
      if (mMonitoringAlertReport) {
        list.push({ label: 'Report', icon: <FileText className="h-4 w-4 text-white" /> });
      } else {
        // Alert is the last item, make it white
        list[list.length - 1].icon = <Siren className="h-4 w-4 text-white" />;
      }
      return list;
    }
    if (mMonitoringOverview) {
      list.push({ label: 'Monitoring Overview', icon: <Activity className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mMonitoringOverviewV2) {
      list.push({ label: 'Monitoring Overview V2', icon: <Activity className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mAlerts) {
      list.push({ label: 'Alert Log', icon: <Siren className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mReporting) {
      list.push({ label: 'Reporting', icon: <BarChart3 className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mBilling) {
      list.push({ label: 'Billing', icon: <DollarSign className="h-4 w-4 text-white" /> });
      return list;
    }

    // Connections
    if (mConnectionsTop || mConnectionsDeep) {
      list.push({ label: 'Connections', icon: <Plug className="h-4 w-4 text-white" /> });
      return list;
    }

    // Users
    if (mUsers && !mUserDetail && !mUserAdd) {
      list.push({ label: 'Users', href: '/users', icon: <UsersIcon className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mUserAdd) {
      list.push({ label: 'Users', href: '/users', icon: <UsersIcon className="h-4 w-4 text-lime" /> });
      list.push({ label: 'Add New User', icon: <UserPlus className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mUserDetail) {
      list.push({ label: 'Users', href: '/users', icon: <UsersIcon className="h-4 w-4 text-lime" /> });
      const userName = user ? `${user.firstName} ${user.lastName}`.trim() || user.email || 'Unnamed User' : 'Unnamed User';
      list.push({ label: userName, icon: <UserIcon className="h-4 w-4 text-white" /> });
      return list;
    }

    // Viewer page with dynamic breadcrumbs based on sidebar selection
    if (mViewer) {
      list.push({ label: 'Viewer', icon: <MonitorIcon className="h-4 w-4" /> });
      
      if (effectiveCustomerId) {
        list.push({ 
          label: customer?.name || 'Unnamed Customer', 
          icon: <Folder className="h-4 w-4 text-lime" />
        });
        
        if (effectiveSiteId) {
          list.push({ 
            label: siteName || 'Unnamed Site', 
            icon: <Building2 className="h-4 w-4 text-lime" />
          });
          
          if (deviceId) {
            list.push({ 
              label: device?.name || 'Unnamed Device', 
              icon: <HardDrive className="h-4 w-4 text-white" /> 
            });
          } else {
            // Site is the last item, make it white
            list[list.length - 1].icon = <Building2 className="h-4 w-4 text-white" />;
          }
        } else {
          // Customer is the last item, make it white
          list[list.length - 1].icon = <Folder className="h-4 w-4 text-white" />;
        }
      } else {
        // Viewer is the last item, make it white
        list[list.length - 1].icon = <MonitorIcon className="h-4 w-4 text-white" />;
      }
      
      return list;
    }

    // Teams
    if (mTeams && !mTeamAdd) {
      list.push({ label: 'Teams', href: '/team', icon: <UsersIcon className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mTeamAdd) {
      list.push({ label: 'Teams', href: '/team', icon: <UsersIcon className="h-4 w-4 text-lime" /> });
      list.push({ label: 'Add New Team', icon: <FolderPlus className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mTeamDetail) {
      list.push({ label: 'Teams', href: '/team', icon: <UsersIcon className="h-4 w-4 text-lime" /> });
      list.push({ label: team?.name || 'Unnamed Team', icon: <UsersIcon className="h-4 w-4 text-white" /> });
      return list;
    }

    // iHub
    if (mIHub) {
      list.push({ label: 'iHub', href: '/ihub', icon: <Network className="h-4 w-4 text-white" /> });
      if (mIHubMonitor) {
        list.push({ label: 'Monitor', icon: <MonitorIcon className="h-4 w-4 text-white" /> });
      } else if (mIHubIntegration) {
        list.push({ label: 'Integration', icon: <Plug className="h-4 w-4 text-white" /> });
      }
      return list;
    }

    // Ingestion
    if (mIngestionLegacy) {
      list.push({ label: 'Ingestion', href: '/ingestion', icon: <Activity className="h-4 w-4 text-white" /> });
      list.push({ label: 'Inbound Webhooks (Legacy)', href: '/ingestion/legacy', icon: <Activity className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mIngestion) {
      list.push({ label: 'Ingestion', href: '/ingestion', icon: <Activity className="h-4 w-4 text-white" /> });
      return list;
    }

    // System (legacy path)
    if (mSystem) {
      list.push({ label: 'Ingestion', href: '/ingestion', icon: <Activity className="h-4 w-4 text-white" /> });
      return list;
    }

    // Alert Audit
    if (mAlertAudit) {
      list.push({ label: 'Alert Audit', href: '/alert-audit', icon: <Siren className="h-4 w-4 text-white" /> });
      return list;
    }

    if (mDashboard) {
      list.push({ label: 'Dashboard', href: '/dashboard', icon: <Home className="h-4 w-4 text-white" /> });
      return list;
    }

    // EdgeLink
    if (mEdgeLink) {
      list.push({ label: 'EdgeLink', href: '/edgelink', icon: <Network className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mEdgeLinkAdd) {
      list.push({ label: 'EdgeLink', href: '/edgelink', icon: <Network className="h-4 w-4 text-lime" /> });
      list.push({ label: 'Add Bridge', icon: <Plus className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mEdgeLinkDetail) {
      list.push({ label: 'EdgeLink', href: '/edgelink', icon: <Network className="h-4 w-4 text-lime" /> });
      list.push({ label: 'Bridge Details', icon: <Network className="h-4 w-4 text-white" /> });
      return list;
    }
    if (mEdgeLinkDeployments) {
      list.push({ label: 'EdgeLink Deployments', icon: <Rocket className="h-4 w-4 text-white" /> });
      return list;
    }

    // Default: Show current path last segment
    const parts = path.split('/').filter(Boolean);
    if (parts.length) {
      list.push({ label: decodeURIComponent(parts[parts.length - 1]), icon: <FileText className="h-4 w-4 text-white" /> });
    }
    // Deduplicate consecutive identical labels/links to avoid accidental duplicates
    const deduped = list.filter((item, idx, arr) => {
      if (idx === 0) return true;
      const prev = arr[idx - 1];
      return !(prev.label === item.label && prev.href === item.href);
    });
    return deduped;
  }, [
    path,
    mCustomers,
    mCustomerDetail,
    mCustomerSite,
    mDevices,
    mDeviceEdit,
    mDeviceCamera,
    mReporting,
    mBilling,
    mAlerts,
    mMonitor,
    mMonitoringOverview,
    mMonitoringOverviewV2,
    mMonitoringAlert,
    mMonitoringAlertReport,
    mAlertSimple,
    mReviewAlert,
    isAlertSimplePath,
    isReviewAlertPath,
    mConnectionsTop,
    mConnectionsDeep,
    mUsers,
    mTeams,
    mTeamDetail,
    mIHub,
    mSystem,
    mIngestion,
    mIngestionLegacy,
    mAlertAudit,
    mSitesList,
    mSiteShallow,
    mSiteNew,
    mViewer,
    mUserDetail,
    mDashboard,
    mEdgeLink,
    mEdgeLinkAdd,
    mEdgeLinkDetail,
    mEdgeLinkDeployments,
    team?.name,
    customer?.name,
    customerIdRoute,
    siteName,
    deviceId,
    effectiveCustomerId,
    effectiveSiteId,
    device?.name,
    cameraName,
    alertShort,
    alertForBreadcrumb,
    user,
    alertIdParam,
    mIHubIntegration,
    mIHubMonitor,
    mTeamAdd,
    mUserAdd,
  ]);

  if (items.length === 0) {
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList> <BreadcrumbSeparator className='opacity-40' >/</BreadcrumbSeparator>
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          const shouldLink = Boolean(item.href && (items.length === 1 || !isLast));
          return (
            <React.Fragment key={idx}>
              <BreadcrumbItem>
                <div className="flex items-center gap-1 font-semibold">
                  {shouldLink ? (
                    <BreadcrumbLink asChild>
                      <Link className='inline-flex items-center justify-start gap-1 ' to={item.href!}>
                        {item.icon ? item.icon : null}
                        <span className='hover:text-lime hover:underline truncate max-w-[20rem]'>{item.label}</span>
                      </Link>
                    </BreadcrumbLink>
                  ) : isLast ? (
                    <p className='inline-flex items-center gap-1 justify-start text-white '>
                      {item.icon ? item.icon : null}
                      <span className='truncate max-w-[20rem]  '>{item.label}</span>
                    </p>
                  ) : (
                    <BreadcrumbPage>
                      <span className='inline-flex items-center gap-1 justify-start '>
                        {item.icon ? item.icon : null}
                        <span className='truncate max-w-[20rem]'>{item.label}</span>
                      </span>
                    </BreadcrumbPage>
                  )}
                  
                  {item.popover && item.popover.content && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="py-3 px-2 hover:bg-lime/10 rounded">
                          <ChevronsUpDown className="h-3 w-3 text-lime hover:text-white" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent 
                        side={item.popover.placement?.split('-')[0] as 'top' | 'bottom' | 'left' | 'right' || 'bottom'}
                        align={item.popover.placement?.includes('-') ? item.popover.placement.split('-')[1] as 'start' | 'center' | 'end' : 'center'}
                        className="w-80 p-0"
                      >
                        {item.popover.content}
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </BreadcrumbItem>
              {!isLast ? <BreadcrumbSeparator className='opacity-40' >/</BreadcrumbSeparator> : null}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
